/**
 * Top-level app. Loads data + config, validates, mounts the scene and overlays.
 */
import { Canvas } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { dataSource } from './data/source';
import { validate } from './data/validate';
import { computeCentrality } from './data/centrality';
import type {
  AppConfig,
  ClusterBy,
  Counter,
  Difficulty,
  GraphNode,
  MediaRefsIndex,
  Move,
  Position,
} from './types/domain';
import type { GraphMode } from './data/build-graph';
import { clusterIdsFor } from './data/cluster';
import { Hud } from './ui/Hud';
import { NavOverlay } from './ui/NavOverlay';
import { Drawer } from './ui/Drawer';
import { GameplanPanel } from './ui/GameplanPanel';
import { ListView } from './ui/ListView';
import { EdgeActions } from './ui/EdgeActions';
import { FeedbackModal } from './ui/FeedbackModal';
import { OnboardingCoach } from './ui/OnboardingCoach';
import { TutorialCard } from './ui/TutorialCard';
import { PanelHandle } from './ui/PanelHandle';
import { Scene } from './graph/Scene';
import { BG_COLOR, initClusterColors } from './zones/palette';
import { useAppStore } from './state/store';
import { installDragListeners } from './state/drag';
import { useViewport } from './ui/useViewport';

type GiFilter =
  | 'all'
  | 'gi'
  | 'gi-only'
  | 'no-gi'
  | 'no-gi-only';

/**
 * Apply EVERY HUD filter to the raw dataset and return the final set
 * that both the canvas (Scene) and the sheet (ListView) consume.
 * Keeping the entire filter chain in one place is what guarantees the
 * list view shows exactly what the canvas renders — turning off
 * specialist difficulty, switching to gi-only, hiding the joint-lock
 * cluster — every one shrinks both surfaces in lockstep.
 *
 * Filter stages, in order:
 *   1. Difficulty / variants-collapse / concept filter (entity-level
 *      attributes the user dials in the HUD).
 *   2. Gi-mode filter on moves — 5-state {all, gi, gi-only, no-gi,
 *      no-gi-only}. Positions don't carry gi.
 *   3. Cluster-aware filter — when a cluster axis is selected, drop
 *      "rendered-kind" nodes that have no value for the axis.
 *      Position-mode drops positions; move-mode drops moves;
 *      off-axis entities pass through (positions in move-mode are
 *      lookup data only, not rendered nodes).
 *   4. Hidden-cluster filter — `hiddenClusterIds` from the cluster
 *      key's row toggles drops every node whose entire cluster
 *      membership is hidden (a node passes if any of its cluster ids
 *      is still visible).
 *   5. Counter derivation — drop counters whose parent move or
 *      result position was filtered out.
 */
type FilteredSets = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  /**
   * Pre-cluster reference set with everything except the cluster
   * filter applied (so gi / difficulty / variants / concepts are
   * already cooked in). buildGraph reads these for its degree /
   * hub / orphan computation so a surviving node keeps the size it
   * would have in the broader graph even when most of its
   * neighbours fell to the cluster filter.
   */
  referencePositions: Position[];
  referenceMoves: Move[];
};

function applyFilters(
  positions: Position[],
  moves: Move[],
  counters: Counter[],
  difficultyFilter: Set<Difficulty>,
  collapseVariants: boolean,
  conceptFilter: Set<string>,
  giFilter: GiFilter,
  mode: GraphMode,
  clusterBy: ClusterBy,
  hiddenClusterIds: Set<string>,
  gameplanRefSets: {
    posIds: Set<string>;
    moveIds: Set<string>;
    counterIds: Set<string>;
  } | null,
  searchRefSets: {
    posIds: Set<string>;
    moveIds: Set<string>;
    counterIds: Set<string>;
  } | null,
): FilteredSets {
  const hasConceptFilter = conceptFilter.size > 0;
  const passesConcepts = (concepts: string[] | undefined) => {
    if (!hasConceptFilter) return true;
    if (!concepts || concepts.length === 0) return false;
    return concepts.some((c) => conceptFilter.has(c));
  };
  const passesDifficulty = (d: Difficulty | undefined) => {
    if (!d) return true; // entities with no difficulty are always visible
    return difficultyFilter.has(d);
  };
  const passesVariant = (isCanonical: boolean | undefined, family: string | undefined) => {
    if (!collapseVariants) return true;
    if (!family) return true; // entities with no family are always shown
    return isCanonical !== false; // undefined or true ⇒ canonical-by-default
  };

  // ----- Stage 1: difficulty / variant / concept -----
  let visiblePositions = positions.filter(
    (p) =>
      passesDifficulty(p.difficulty) &&
      passesVariant(p.isCanonical, p.family) &&
      passesConcepts(p.concepts),
  );
  let visibleIds = new Set(visiblePositions.map((p) => p.id));
  let visibleMoves = moves.filter(
    (m) =>
      passesDifficulty(m.difficulty) &&
      passesVariant(m.isCanonical, m.family) &&
      passesConcepts(m.concepts) &&
      visibleIds.has(m.from) &&
      visibleIds.has(m.to),
  );

  // ----- Stage 2: gi filter on moves -----
  if (giFilter !== 'all') {
    visibleMoves = visibleMoves.filter((m) => {
      const g = m.gi ?? 'both';
      if (giFilter === 'gi') return g === 'gi' || g === 'both';
      if (giFilter === 'gi-only') return g === 'gi';
      if (giFilter === 'no-gi') return g === 'no-gi' || g === 'both';
      if (giFilter === 'no-gi-only') return g === 'no-gi';
      return true;
    });
  }

  // Snapshot the post-gi sets BEFORE the cluster-aware cuts — they
  // serve as the reference for degree-centrality (see FilteredSets
  // doc). Even when the cluster filter shrinks the surviving set
  // hard, each remaining node keeps its true place in the broader
  // topology.
  const referencePositions = [...visiblePositions];
  const referenceMoves = [...visibleMoves];

  // ----- Stage 3 + 4: cluster-aware + hidden-cluster filter -----
  if (clusterBy !== 'none') {
    // Lookups for clusterIdsFor — built from the post-stage-2 sets so
    // the cluster check sees coherent references.
    const positionsById = new Map(visiblePositions.map((p) => [p.id, p]));
    const movesById = new Map(visibleMoves.map((m) => [m.id, m]));

    const passesCluster = (
      id: string,
      kind: 'position' | 'move',
    ): boolean => {
      const ids = clusterIdsFor(
        { id, kind } as GraphNode,
        clusterBy,
        positionsById,
        movesById,
      );
      if (ids.length === 0) return false; // no value on this axis
      // Hidden filter: keep the node if at least one of its cluster
      // ids is NOT hidden. Multi-membership (concepts) so we don't
      // wipe out moves that share one hidden tag with their other
      // visible tags.
      if (hiddenClusterIds.size === 0) return true;
      return ids.some((c) => !hiddenClusterIds.has(c));
    };

    if (mode === 'position') {
      visiblePositions = visiblePositions.filter((p) =>
        passesCluster(p.id, 'position'),
      );
      // Moves whose endpoint position was filtered out must drop too.
      const surviving = new Set(visiblePositions.map((p) => p.id));
      visibleMoves = visibleMoves.filter(
        (m) => surviving.has(m.from) && surviving.has(m.to),
      );
      visibleIds = surviving;
    } else {
      visibleMoves = visibleMoves.filter((m) =>
        passesCluster(m.id, 'move'),
      );
    }
  }

  // ----- Stage 5: counters derived from filtered moves + positions -----
  const visibleMoveIds = new Set(visibleMoves.map((m) => m.id));
  let visibleCounters = counters.filter(
    (c) => visibleMoveIds.has(c.intercepts) && visibleIds.has(c.result),
  );

  // ----- Stage 6: gameplan filter -----
  // Narrow the visible set to exactly the entities referenced by the
  // active gameplan's lines — plus the endpoint positions of any
  // referenced move (so its edge keeps both ends) and the parent move of
  // any referenced counter (so the interception marker has an arc to sit
  // on). The reference sets are left untouched so node sizing still
  // reflects true graph connectivity inside the filtered view.
  // Applied for the gameplan eye and, identically, the Search eye — both
  // narrow the canvas to "exactly these entities". Running both simply
  // intersects, which is the intuitive result when each is on.
  const narrowTo = (refSets: {
    posIds: Set<string>;
    moveIds: Set<string>;
    counterIds: Set<string>;
  }) => {
    const allowedMoveIds = new Set(refSets.moveIds);
    const allowedPos = new Set(refSets.posIds);
    for (const c of counters) {
      if (refSets.counterIds.has(c.id)) {
        allowedMoveIds.add(c.intercepts);
        allowedPos.add(c.result);
      }
    }
    for (const m of moves) {
      if (allowedMoveIds.has(m.id)) {
        allowedPos.add(m.from);
        allowedPos.add(m.to);
      }
    }
    visiblePositions = visiblePositions.filter((p) => allowedPos.has(p.id));
    const survPos = new Set(visiblePositions.map((p) => p.id));
    visibleMoves = visibleMoves.filter(
      (m) =>
        allowedMoveIds.has(m.id) && survPos.has(m.from) && survPos.has(m.to),
    );
    const survMoveIds = new Set(visibleMoves.map((m) => m.id));
    visibleCounters = visibleCounters.filter(
      (c) =>
        refSets.counterIds.has(c.id) &&
        survMoveIds.has(c.intercepts) &&
        survPos.has(c.result),
    );
  };

  if (gameplanRefSets) narrowTo(gameplanRefSets);
  if (searchRefSets) narrowTo(searchRefSets);

  return {
    positions: visiblePositions,
    moves: visibleMoves,
    counters: visibleCounters,
    referencePositions,
    referenceMoves,
  };
}

export default function App() {
  const [positions, setPositions] = useState<Position[]>([]);
  const [moves, setMoves] = useState<Move[]>([]);
  const [counters, setCounters] = useState<Counter[]>([]);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [mediaRefs, setMediaRefs] = useState<MediaRefsIndex | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setLoaded = useAppStore((s) => s.setLoaded);
  const difficultyFilter = useAppStore((s) => s.difficultyFilter);
  const collapseVariants = useAppStore((s) => s.collapseVariants);
  const conceptFilter = useAppStore((s) => s.conceptFilter);
  const listViewOpen = useAppStore((s) => s.listViewOpen);
  const gameplanViewOpen = useAppStore((s) => s.gameplanViewOpen);
  const gameplanFilter = useAppStore((s) => s.gameplanFilter);
  const activeGameplanId = useAppStore((s) => s.activeGameplanId);
  const gameplans = useAppStore((s) => s.gameplans);
  const searchRefSets = useAppStore((s) => s.searchRefSets);
  const activeLineId = useAppStore((s) => s.activeLineId);
  const selection = useAppStore((s) => s.selection);
  const drawerOpen = useAppStore((s) => s.drawerOpen);
  const { isMobile, isPortrait } = useViewport();
  // When the side-panels render as a BOTTOM sheet (mobile portrait),
  // the outer flex container must flow as a column so the panel's
  // animated height squeezes the canvas pane upward. Side mode keeps
  // the historical row layout where width-grow squeezes left/right.
  const bottomSheetMode = isMobile && isPortrait;
  const giFilter = useAppStore((s) => s.giFilter);
  const mode = useAppStore((s) => s.mode);
  const clusterBy = useAppStore((s) => s.clusterBy);
  const hiddenClusterIds = useAppStore((s) => s.hiddenClusterIds);

  // One-time install of the window-level drag tracker. Module-level state
  // so hover/click handlers can early-return during a drag without
  // re-rendering React.
  useEffect(() => installDragListeners(), []);

  // Clear any in-flight hover state at the moment a press becomes a
  // drag (> 5px of movement). Done on 'bjjmap:dragstart' rather than on
  // pointerdown so a plain click (press → release with no movement)
  // doesn't accidentally collapse expanded sleeves or hide cards out
  // from under the user. Pure pan / orbit gestures still clean up:
  // first 5px of cursor motion triggers the clear.
  useEffect(() => {
    const onDragStart = () => {
      useAppStore.setState({
        hoverId: null,
        previewMoveId: null,
        hoveredSleeveIndex: null,
      });
    };
    window.addEventListener('bjjmap:dragstart', onDragStart);
    return () => window.removeEventListener('bjjmap:dragstart', onDragStart);
  }, []);

  // ----- Mobile back-gesture interception -----
  // On phones a back swipe/press should peel off the current overlay
  // (close the gameplan/list panel, then deselect a node) instead of
  // leaving the page. We keep one dummy history entry alive while any
  // overlay is open; popstate consumes it and closes a single level,
  // re-arming if more remain. Gated to mobile so desktop browser-back
  // keeps navigating normally.
  const backGuardRef = useRef(false);
  const anyOverlayOpen =
    selection.kind !== 'none' ||
    listViewOpen ||
    gameplanViewOpen ||
    drawerOpen;

  useEffect(() => {
    if (!isMobile) return;
    if (anyOverlayOpen && !backGuardRef.current) {
      window.history.pushState({ bjjmapBackGuard: Date.now() }, '');
      backGuardRef.current = true;
    } else if (!anyOverlayOpen) {
      backGuardRef.current = false;
    }
  }, [isMobile, anyOverlayOpen]);

  useEffect(() => {
    if (!isMobile) return;
    const onPop = () => {
      const s = useAppStore.getState();
      const open =
        s.selection.kind !== 'none' ||
        s.listViewOpen ||
        s.gameplanViewOpen ||
        s.drawerOpen;
      backGuardRef.current = false;
      if (!open) return; // nothing to peel → allow real navigation
      // Peel order (mobile UX batch, item #16):
      //   1) deselect node     — most local action, frees the user
      //                          from a "Detail tab" before anything
      //                          else changes shape.
      //   2) close plan        — exits a gameplan back to the index,
      //                          or out of the plan view entirely if
      //                          they're at the index.
      //   3) close drawer      — collapses the side / bottom panel.
      // The List view is part of the drawer's content, so closing
      // it is folded into the "close drawer" step: if the user is
      // looking at the list, the same back gesture closes the
      // drawer (and the list with it).
      if (s.selection.kind !== 'none') s.setSelection({ kind: 'none' });
      else if (s.gameplanViewOpen) s.setGameplanViewOpen(false);
      else if (s.listViewOpen) {
        s.setListViewOpen(false);
        s.setDrawerOpen(false);
      } else if (s.drawerOpen) s.setDrawerOpen(false);
      // Re-arm if anything's still open so the next back is intercepted too.
      const after = useAppStore.getState();
      const stillOpen =
        after.selection.kind !== 'none' ||
        after.listViewOpen ||
        after.gameplanViewOpen ||
        after.drawerOpen;
      if (stillOpen) {
        window.history.pushState({ bjjmapBackGuard: Date.now() }, '');
        backGuardRef.current = true;
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isMobile]);

  useEffect(() => {
    (async () => {
      try {
        const [p, m, c, cfg, mr] = await Promise.all([
          dataSource.positions(),
          dataSource.moves(),
          dataSource.counters(),
          dataSource.config(),
          dataSource.mediaRefs(),
        ]);
        const v = validate(p, m, c);
        if (!v.ok) {
          setError(`data invalid: ${v.errors.slice(0, 3).join('; ')}`);
          return;
        }
        if (v.warnings.length > 0) console.warn('data warnings', v.warnings);
        // Seed the golden-angle cluster-colour allocator with the FULL
        // universe of family + concept ids (positions AND moves) before
        // anything renders. Ranking over the complete dataset (not the
        // filtered subset) is what keeps a given family/concept's colour
        // stable when the user toggles filters. See palette.ts §
        // initClusterColors / ADR-039.
        const families = [
          ...p.map((x) => x.family),
          ...m.map((x) => x.family),
        ].filter((f): f is string => !!f);
        const concepts = [
          ...p.flatMap((x) => x.concepts ?? []),
          ...m.flatMap((x) => x.concepts ?? []),
        ];
        initClusterColors(families, concepts);
        setPositions(p);
        setMoves(m);
        setCounters(c);
        setConfig(cfg);
        setMediaRefs(mr);
        setLoaded(true);
      } catch (e) {
        setError(String(e));
      }
    })();
  }, [setLoaded]);

  // Filter the raw arrays before they're handed to the graph builder.
  // Memoized on the source data + filter knobs so the force sim only
  // re-runs when the visible set actually changes.
  // When the gameplan filter is on, collect the position / move / counter
  // ids referenced anywhere in the active gameplan's lines. Null when the
  // filter is off, the gameplan view is closed, or no plan is active — in
  // which case applyFilters skips its gameplan stage entirely.
  const gameplanRefSets = useMemo(() => {
    // Applies whenever a plan is active and the toggle is on — regardless
    // of which panel tab (Plan / Detail / List) is currently showing, so
    // drilling into a node's detail doesn't drop the filter.
    if (!gameplanFilter || activeGameplanId === null) {
      return null;
    }
    const gp = gameplans.find((g) => g.id === activeGameplanId);
    if (!gp) return null;
    // Scope to the active LINE when one is selected (switching lines
    // reloads the graph); fall back to the whole plan only when no line
    // is active.
    const activeLine =
      activeLineId !== null
        ? gp.lines.find((l) => l.id === activeLineId) ?? null
        : null;
    const sourceLines = activeLine ? [activeLine] : gp.lines;
    const posIds = new Set<string>();
    const moveIds = new Set<string>();
    const counterIds = new Set<string>();
    for (const line of sourceLines) {
      for (const node of line.nodes) {
        const ref = node.ref;
        if (ref.kind === 'position') posIds.add(ref.id);
        else if (ref.kind === 'move') moveIds.add(ref.id);
        else {
          counterIds.add(ref.id);
          moveIds.add(ref.moveId);
        }
      }
    }
    return { posIds, moveIds, counterIds };
  }, [gameplanFilter, activeGameplanId, activeLineId, gameplans]);

  const filtered = useMemo(
    () =>
      applyFilters(
        positions,
        moves,
        counters,
        difficultyFilter,
        collapseVariants,
        conceptFilter,
        giFilter,
        mode,
        clusterBy,
        hiddenClusterIds,
        gameplanRefSets,
        searchRefSets,
      ),
    [
      positions,
      moves,
      counters,
      difficultyFilter,
      collapseVariants,
      conceptFilter,
      giFilter,
      mode,
      clusterBy,
      hiddenClusterIds,
      gameplanRefSets,
      searchRefSets,
    ],
  );

  const degree = useMemo(() => {
    if (filtered.positions.length === 0) return new Map<string, number>();
    return computeCentrality(filtered.positions, filtered.moves, filtered.counters).degree;
  }, [filtered]);

  if (error) {
    return (
      <div style={{ padding: 24, color: '#ff5d6c', fontFamily: 'monospace' }}>
        {error}
      </div>
    );
  }

  if (positions.length === 0 || !config) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', color: '#cad3df' }}>
        loading...
      </div>
    );
  }

  return (
    <>
      {/*
        Split-pane layout: the canvas pane and the drawer pane are flex
        siblings. The drawer pane animates its own width (and is collapsed
        to 0 when closed), which forces the canvas pane (`flex: 1`) to
        re-flow into the remaining space. R3F's ResizeObserver on the
        canvas wrapper picks up the width change and updates the camera
        aspect each frame, so the constellation re-centers as the drawer
        widens. This is guaranteed non-overlapping by construction —
        there is no z-index race between two fixed-position panels.
      */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          // Row on desktop / landscape (side drawer squeezes from the
          // right). Column on mobile portrait (bottom sheet squeezes
          // from below). The panel component itself doesn't change —
          // it lives as an in-flow flex sibling either way.
          flexDirection: bottomSheetMode ? 'column' : 'row',
          background: BG_COLOR,
          overflow: 'hidden',
        }}
      >
        <div
          data-tour="canvas"
          style={{
            flex: 1,
            // Both min-axes are zeroed so the canvas pane can shrink
            // along whichever axis the panel is currently growing on.
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
          }}
        >
          {/* NavOverlay is positioned absolutely INSIDE this pane so it
              slides with the drawer (which sits as a flex sibling and
              squeezes this pane when it opens/widens). */}
          {/* NavOverlay's cluster-key shows ALL clusters present in
              the pre-cluster-filter data so the user can un-hide a
              hidden cluster — pass the reference sets instead of the
              fully filtered ones. */}
          <NavOverlay
            positions={filtered.referencePositions}
            moves={filtered.referenceMoves}
          />
          <Canvas
            camera={{ position: [0, 0, 82], fov: 50, near: 0.1, far: 700 }}
            // Line raycast threshold. Default 1.0 is too generous and
            // causes stray hits; 0.5 is tight enough that hover only
            // registers near the visible line, while still being just
            // wider than the half-spacing of fanned sub-arcs (so the
            // dead zone in the middle of an expanded sleeve stays
            // small enough for the hover debounce to bridge).
            raycaster={{
              params: { Line: { threshold: 0.5 } } as unknown as THREE.RaycasterParameters,
            }}
            style={{
              width: '100%',
              height: '100%',
              background: BG_COLOR,
            }}
            dpr={[1, 2]}
          >
            <color attach="background" args={[BG_COLOR]} />
            <Scene
              positions={filtered.positions}
              moves={filtered.moves}
              counters={filtered.counters}
              config={config}
              referencePositions={filtered.referencePositions}
              referenceMoves={filtered.referenceMoves}
            />
          </Canvas>
        </div>
        {gameplanViewOpen ? (
          // Gameplan editor needs the FULL (unfiltered) entity sets so
          // line labels resolve regardless of canvas filters. Media
          // refs flow through for the cheatsheet export.
          <GameplanPanel
            positions={positions}
            moves={moves}
            counters={counters}
            mediaRefs={mediaRefs}
          />
        ) : listViewOpen ? (
          <ListView
            positions={filtered.positions}
            moves={filtered.moves}
            counters={filtered.counters}
          />
        ) : selection.kind !== 'none' ? (
          // Only show the Detail drawer when there's actually something
          // selected — otherwise the right pane stays empty (canvas only).
          // NOTE: this renders during the onboarding tour too, so "tap any
          // body to open it" actually shows the node's detail. The coach
          // keeps the drawer COLLAPSED on its own auto-selection (see
          // CoachNodeAnchor) so the map stays full until the user opens it.
          <Drawer
            positions={filtered.positions}
            moves={filtered.moves}
            counters={filtered.counters}
            degree={degree}
            mediaRefs={mediaRefs}
          />
        ) : null}
      </div>
      <PanelHandle />
      <EdgeActions
        positions={positions}
        moves={moves}
        counters={counters}
      />
      <Hud />
      <FeedbackModal />
      {/* Coach-marks / lens nudge — reads its own gates; renders nothing
          until a flow is active. Kept out of the front-door path so it
          only fires once the map is actually visible. */}
      <OnboardingCoach />
      {/* Line-playback control — compact card locked above the bottom-right
          EdgeActions stack. Renders only while a line is playing. Full
          entity sets so node names resolve regardless of canvas filters. */}
      <TutorialCard positions={positions} moves={moves} counters={counters} />
    </>
  );
}

// PanelHandle moved to ./ui/PanelHandle.tsx — mode-aware (side caret
// on desktop / landscape, top-of-sheet bar on mobile portrait).
